import { FC, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Box, Typography, Stack, Button, Checkbox } from '@mui/material';

type FilterOption = {
  label: ReactNode;
  value: string;
};
export type GroupedMultiSelectOptions = Map<FilterOption, FilterOption[]>;

export interface GroupedMultiSelectFilterProps {
  options: GroupedMultiSelectOptions;
  selectedOptions: GroupedMultiSelectOptions;
  onSave: (selected: GroupedMultiSelectOptions) => void;
  onReset: () => void;
  filterLabel?: string;
  setIsFilterOpen?: (open: boolean) => void;
  isFilterOpen?: boolean;
}

const GroupedMultiSelectFilter: FC<GroupedMultiSelectFilterProps> = (props) => {
  const { options, selectedOptions, onSave, onReset, setIsFilterOpen, isFilterOpen, filterLabel } =
    props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [localSelectedOptions, setLocalSelectedOptions] =
    useState<GroupedMultiSelectOptions>(selectedOptions);

  // Sync local state when selectedOptions prop changes
  useEffect(() => {
    setLocalSelectedOptions(selectedOptions);
  }, [selectedOptions]);

  // Close filter on outside click
  useEffect(() => {
    if (!isFilterOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideClick =
        containerRef.current && !containerRef.current.contains(event.target as Node);
      if (isOutsideClick) {
        setIsFilterOpen?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen, setIsFilterOpen]);

  const isOptionSelected = useCallback(
    (optionGroup: FilterOption, option: FilterOption): boolean => {
      const optionGroupKey = [...localSelectedOptions.keys()].find(
        (k) => k.value === optionGroup.value,
      );
      if (!optionGroupKey) return false;
      const selectedOptionsForGroup = localSelectedOptions.get(optionGroupKey) || [];
      return selectedOptionsForGroup.some((opt) => opt.value === option.value);
    },
    [localSelectedOptions],
  );

  const toggleOption = useCallback(
    (group: FilterOption, option: FilterOption) => {
      const newSelectedOptions = new Map(localSelectedOptions);

      // Find existing group key or use the provided one (for first selection in a group)
      const groupKey = [...newSelectedOptions.keys()].find((k) => k.value === group.value) || group;
      const currentOptions = newSelectedOptions.get(groupKey) || [];

      const isAlreadySelected = currentOptions.some((opt) => opt.value === option.value);
      const updatedOptions = isAlreadySelected
        ? currentOptions.filter((opt) => opt.value !== option.value)
        : [...currentOptions, option];

      if (updatedOptions.length > 0) {
        newSelectedOptions.set(groupKey, updatedOptions);
      } else {
        newSelectedOptions.delete(groupKey);
      }

      setLocalSelectedOptions(newSelectedOptions);
    },
    [localSelectedOptions],
  );

  const handleSave = useCallback(() => {
    onSave(localSelectedOptions);
  }, [localSelectedOptions, onSave]);

  const handleReset = useCallback(() => {
    setLocalSelectedOptions(new Map());
    onReset();
  }, [onReset]);

  const optionsArray = Array.from(options.entries());

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        border: '1px solid #E8EAEB',
        minWidth: '180px',
        width: 'fit-content',
        boxShadow: '3px 4px 4.3px 0px #0000001A',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px="12px"
        py="8px"
        sx={{
          borderBottom: '1px solid #737F86',
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer', flex: 1 }}>
          <Typography fontSize="12px" fontWeight={700} color="#6C6C6C">
            {filterLabel || 'Filter'}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1.5}>
          <Button
            onClick={handleReset}
            sx={{
              padding: 0,
              minWidth: 'auto',
              fontSize: '12px',
              fontWeight: 700,
              color: '#2F736E',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              padding: 0,
              minWidth: 'auto',
              fontSize: '12px',
              fontWeight: 700,
              color: '#2F736E',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <Box>
        {/* Groups */}
        {optionsArray.map(([group, groupOptions]) => {
          return (
            <Box key={group.value} sx={{ borderTop: '1px solid #E8EAEB', p: '8px 12px' }}>
              {/* Group Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontSize="14px" fontWeight={500} color="#6C6C6C">
                  {group.label}
                </Typography>
              </Stack>

              {/* Group Options */}
              <Box>
                <Stack gap={1.5} py={2} sx={{ maxHeight: '180px', overflowY: 'auto' }}>
                  {groupOptions.map((option) => (
                    <Stack
                      key={option.value}
                      direction="row"
                      alignItems="center"
                      sx={{ paddingLeft: '2px', cursor: 'pointer' }}
                      onClick={() => toggleOption(group, option)}
                      gap={1.5}
                    >
                      <Checkbox
                        checked={isOptionSelected(group, option)}
                        onChange={() => toggleOption(group, option)}
                        sx={{
                          height: '18px',
                          width: '18px',
                          padding: '4px',
                          color: '#162C36',
                          '&.Mui-checked': {
                            color: '#162C36',
                          },
                        }}
                      />
                      <Typography fontSize="14px" fontWeight={500} color="#162936">
                        {option.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default GroupedMultiSelectFilter;
